import {TransactionEntryLine} from '@/types/transaction/TransactionEntryLine'
import {TransactionLineCreationInfo} from '@/types/transaction/TransactionLineCreationInfo'
import {patch, post} from '@/services/infrastructure/api.service'
import emitter from '@/services/infrastructure/emitter'
import {TRANSACTION_CREATED} from '@/types/transaction/TransactionEvent'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import {computed, nextTick, ref} from 'vue'
import {validateEntryLines} from '@/transaction/create-transaction/EntryLineValidator'
import {LINE_COLUMN} from '@/transaction/create-transaction/EntryLineInstance'
import {CreateTransEntryInstances} from '@/transaction/create-transaction/QuickTransEntriesInstances'
import {EntryLineValidationResult} from '@/transaction/create-transaction/EntryLineValidationResult'
import {defaultVatOption} from '@/vat/types/VatOptions'
import {calculateVatAmount, useVatService} from '@/vat/vat.service'
import {deleteTransaction, postTransaction} from '@/transaction/transaction.service'
import {notifyError} from '@/services/infrastructure/notification.service'
import {ResourceLink} from '@/resource-link/resourceLink'
import {checkLinesDecimalAmount} from '@/transaction/TransactionUtilities'
import {PAYMENT_CREATED} from '@/bank/bank.events'
import {entryLinesToTransactionLines} from '@/transaction/transaction.mapper'
import {TransactionCommandCreate} from '@/transaction/types/TransactionCommands'
import {Fulfillment} from '@/assignments/Assignment.type'
import {v4 as uuidv4} from 'uuid'
import {duplicateInvoiceLineUuids} from '@/transaction/create-transaction/duplicateInvoiceWarnings'

const lastSelectedDate = ref(new Date())
const TRANSACTIONS_PATH = '/transactions'
const newEntryLine = (entryLineIndex: number = 0): TransactionEntryLine => ({
    entryUuid: uuidv4(),
    date: lastSelectedDate.value,
    invoiceDueDate: undefined,
    description: '',
    amount: 0,
    debitAccount: undefined,
    creditAccount: undefined,
    vatOptionIdDebit: 1,
    vatOptionIdCredit: 1,
    tags: [],
    subAccountLines: [],
    entryLineIndex
})

const transactionEntryLines = ref<TransactionEntryLine[]>([newEntryLine()])
const fulfillment = ref<Fulfillment | undefined>(undefined)
const uuid = ref('')
const resourceLinks = ref<ResourceLink[]>([])
const attachments = ref<Array<DocumentMeta>>([])
const {getVatOptionById} = useVatService()

const entriesValidationResult = computed<EntryLineValidationResult>(() => {
    return validateEntryLines(transactionEntryLines.value)
})

const isDirty = computed(() => {
    return !isTransactionEntryLineEmpty.value
})

const checkIfTransactionEntryLineEmpty = (line: TransactionEntryLine) => {
    return line.description === ''
        && line.amount === 0
        && !line.debitAccount
        && line.vatOptionIdDebit === defaultVatOption.id
        && !line.creditAccount
        && line.vatOptionIdCredit === defaultVatOption.id
        && !line.invoiceNo
        && !line.invoiceUuid
        && !line.kid
        && !line.bban
        && line.subAccountLines.length === 0
        && !line.accrualAccountNo
        && !line.accrualDuration
        && !line.accrualStartMonth
        && !line.accrualStartYear
        && !line.paymentDate
}

const isTransactionEntryLineEmpty = computed(() => transactionEntryLines.value.every(line => checkIfTransactionEntryLineEmpty(line)))

const hashFromTransaction = (lines: TransactionLineCreationInfo[], documentMetas: Array<DocumentMeta>): string => {
    return JSON.stringify({
        lines: lines,
        documentMetas: documentMetas
    })
}

const updateLineAtIndex = (index: number, line: TransactionEntryLine) => {
    transactionEntryLines.value[index] = line
    return transactionEntryLines
}

const newBlankLine = (newLineIndex: number) => {
    const newLine = newEntryLine(newLineIndex)
    if (newLineIndex > 0) {
        //Copy date from prev. line
        newLine.date = transactionEntryLines.value[newLineIndex - 1].date
    }
    transactionEntryLines.value.splice(newLineIndex, 0, newLine)
    updateIndices()
}

const copyLine = (line: TransactionEntryLine, index: number) => {
    const newLine = {...line}
    transactionEntryLines.value.splice(index, 0, newLine)
    updateIndices()
}

const copyLineAtIndex = (index: number) => {
    const currentLine = {...transactionEntryLines.value[index]}
    transactionEntryLines.value.splice(index + 1, 0, currentLine)
    updateIndices()
}

const deleteLineAtIndex = (index: number) => {
    transactionEntryLines.value.length > 1 ? transactionEntryLines.value.splice(index, 1) : transactionEntryLines.value[0] = newEntryLine()
    updateIndices()
}

const updateIndices = () => {
    transactionEntryLines.value.map((tl, index) =>
        tl.entryLineIndex = index
    )
}

const deleteLine = (tel: TransactionEntryLine) => {
    const newLines = transactionEntryLines.value.filter(item => tel.entryLineIndex !== item.entryLineIndex)

    transactionEntryLines.value = newLines.length > 0 ? newLines : [newEntryLine()]
    updateIndices()
}

const vatAmounts = computed(() => {
    const amounts = {debit: 0, credit: 0}

    transactionEntryLines.value.forEach(line => {
        const debitOption = getVatOptionById(line.vatOptionIdDebit)
        if (debitOption) amounts.debit += calculateVatAmount(debitOption, line.amount)

        const creditOption = getVatOptionById(line.vatOptionIdCredit)
        if (creditOption) amounts.credit += calculateVatAmount(creditOption, line.amount)
    })

    return amounts
})

const moveFocusBetweenTransactions = (index: number, column: LINE_COLUMN, entries: CreateTransEntryInstances) => {
    nextTick(() => {
        const newRow = entries.value && entries.value[index + 1]
        if (newRow) {
            newRow.moveFocusToColumn(column, 0)
        } else {
            console.error('Unable to focus new row')
        }
    }).then()
}

const reset = () => {
    uuid.value = ''
    transactionEntryLines.value = [newEntryLine()]
    attachments.value = []
    resourceLinks.value = []
    fulfillment.value = undefined
    duplicateInvoiceLineUuids.value = []
}

// TODO too much responsibilities
const saveTransactionInternal = async (documentMetas: Array<DocumentMeta>, lines: TransactionLineCreationInfo[], uuid?: string, payNow?: boolean) => {

    const transaction = {
        documentMetaUuids: documentMetas.map(d => d.uuid),
        transactionLines: checkLinesDecimalAmount(lines),
        resourceLinks: resourceLinks.value,
        payNow
    }
    if (uuid) {
        return await patch(TRANSACTIONS_PATH,
            {
                uuid: uuid,
                ...transaction
            }
        ).then(response => {
            emitter.emit(TRANSACTION_CREATED)
            reset()
            return response
        })
    } else {
        await post(TRANSACTIONS_PATH, transaction).then(response => {
            if (payNow) emitter.emit(PAYMENT_CREATED)
            emitter.emit(TRANSACTION_CREATED)
            reset()
            return response
        })
    }
}

// @ts-ignore
if (window.Cypress) {
    // @ts-ignore
    window.saveTransaction = saveTransactionInternal
}

export const useTransactionCreator = () => {

    const entryLinesAreValid = computed(() => {
        return entriesValidationResult.value.valid
    })

    const saveTransaction = async (documentMetas: Array<DocumentMeta>, lines: TransactionLineCreationInfo[], uuid?: string, payNow?: boolean) => {
        const result = await saveTransactionInternal(documentMetas, lines, uuid, payNow)
        if (result) {
            if (result.isRight()) return result.value
            notifyError({title: 'Feilet', description: {descriptionMessage: `Klarte ikke å oppdatere transaksjon med id: ${uuid}`, error: result.error.detail}})
        }
    }

    const saveTransactionFromState = async (payNow: boolean) => {
        if (!entryLinesAreValid.value) {
            console.error('Transaction invalid', entriesValidationResult.value)
            throw new Error('Transaction invalid')
        }
        const command: TransactionCommandCreate = {
            documentMetaUuids: attachments.value.map((doc) => doc.uuid),
            transactionLines: entryLinesToTransactionLines(transactionEntryLines.value),
            payNow: payNow,
            resourceLinks: resourceLinks.value,
            fulfillment: fulfillment.value
        }
        await postTransaction(command)
        reset()
        emitter.emit(TRANSACTION_CREATED)
    }


    return {
        deleteTransaction,
        reset,
        transactionEntryLines,
        entryLinesAreValid,
        attachments,
        fulfillment,
        updateIndices,
        saveTransaction,
        vatAmounts,
        resourceLinks,
        saveTransactionFromState,
        isDirty
    }
}

export {
    updateLineAtIndex,
    newBlankLine,
    copyLine,
    copyLineAtIndex,
    deleteLine,
    deleteLineAtIndex,
    hashFromTransaction,
    newEntryLine,
    moveFocusBetweenTransactions,
    lastSelectedDate,
    entriesValidationResult,
    checkIfTransactionEntryLineEmpty,
    isTransactionEntryLineEmpty,
    attachments
}